import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAppState } from "../../context/AppStateContext";
import {
  canAssignEngineer,
  findEngineerMaster,
  isEngineerLinkageComplete,
} from "../../utils/engineer";

export function EngineerTab() {
  const { id } = useParams<{ id: string }>();
  const { getById, engineerMasters, updateEngineer, finalizeEngineer } = useAppState();
  const app = id ? getById(id) : undefined;
  const [linkError, setLinkError] = useState<string | null>(null);

  if (!app) return null;
  const link = app.engineer ?? { engineerMasterId: null };
  const gate = canAssignEngineer(app);

  return (
    <>
      <p className="tab-intro">
        <strong>Engineer assignment.</strong> Engineers are registered under{" "}
        <Link to="/engineer-onboarding">Engineer onboarding</Link>. Here you select who covers this beneficiary and record
        the assignment. Same upstream checks as subcontractor assignment apply when this section is locked.
      </p>

      <div className="card card--gov card--elevated card--subcontractor">
        <h2 className="card__title card__title--gov">Engineer</h2>

        {!gate ? (
          <div className="panel-locked">
            <p className="panel-locked__title">This section is locked</p>
            <ul className="panel-locked__list">
              <li>Complete HO validation, Aadhaar seeding, and ICICI + MOU on the respective tabs.</li>
              <li>Resolve any active hold on the file.</li>
              <li>When Bills is enabled, confirm seeding re-verification for each work-done bill there.</li>
            </ul>
          </div>
        ) : (
          <>
            {link.linkedAt ? (
              <p className="alert alert--success">
                <strong>On record.</strong> Engineer assigned at {link.linkedAt.slice(0, 16).replace("T", " ")}. You may
                change the selected engineer; use <strong>Record engineer</strong> again after changes.
              </p>
            ) : null}
            {linkError ? (
              <p className="alert alert--danger" role="alert">
                {linkError}
              </p>
            ) : null}

            <h3 className="form-section-title">Registered engineer</h3>
            <div className="field field--full">
              <label htmlFor="eng-master-select">Engineer</label>
              <select
                id="eng-master-select"
                value={link.engineerMasterId ?? ""}
                onChange={(e) => {
                  setLinkError(null);
                  const v = e.target.value;
                  void updateEngineer(app.id, {
                    engineerMasterId: v === "" ? null : v,
                  });
                }}
              >
                <option value="">— Select an engineer —</option>
                {engineerMasters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.fullName}
                    {m.phone ? ` · ${m.phone}` : ""}
                  </option>
                ))}
              </select>
              <p className="text-muted" style={{ marginTop: "0.35rem", fontSize: "0.8125rem" }}>
                Missing someone?{" "}
                <Link to="/engineer-onboarding" className="btn btn--link btn--sm" style={{ padding: 0, verticalAlign: "baseline" }}>
                  Open Engineer onboarding
                </Link>
              </p>
            </div>

            <p className="section-lead section-lead--tight">
              Required: an engineer with full name and phone on file. Click below to record the assignment in the database.
            </p>
            <div className="toolbar-row">
              <button
                type="button"
                className="btn btn--primary btn--tg btn--lg"
                onClick={() => {
                  void (async () => {
                    setLinkError(null);
                    const master = findEngineerMaster(engineerMasters, link.engineerMasterId);
                    if (!isEngineerLinkageComplete(link, master)) {
                      setLinkError(
                        "Choose an engineer from the master list. Register them under Engineer onboarding first if they are not listed.",
                      );
                      return;
                    }
                    await finalizeEngineer(app.id);
                  })();
                }}
              >
                {link.linkedAt ? "Update engineer assignment" : "Record engineer against beneficiary"}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
